import { useState } from 'react'
import { CheckCircle2, Circle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { toUiDealStage } from '@/adapters/deals'
import { Button } from '@/components/ui/Button'
import { GESTOR_SETOR_LABELS } from '@/config/gestorAccess'
import { GestorEntradaKanbanSync } from '@/components/Gestores/GestorEntradaKanbanSync'
import {
  ENTRADA_STEP_DESCRIPTIONS,
  ENTRADA_STEP_LABELS,
  MANUAL_ENTRADA_STEP_KEYS,
  type ManualEntradaStepKey,
} from '@/components/Gestores/gestorClienteEntradaConfig'
import type { GestorEntradaDetail } from '@/services/gestoresService'
import { gestoresService } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type Props = {
  entrada: GestorEntradaDetail
  onUpdated: (entrada: GestorEntradaDetail) => void
}

function formatSla(dueAt: string | null) {
  if (!dueAt) return null
  const due = new Date(dueAt)
  const diffMs = due.getTime() - Date.now()
  const overdue = diffMs < 0
  const hours = Math.abs(Math.round(diffMs / (60 * 60 * 1000)))
  const label = overdue ? `Handover atrasado há ${hours}h` : `Handover vence em ${hours}h`
  return { label, overdue }
}

function isDealAwaitingHandoverCompletion(dealEtapa: string) {
  return toUiDealStage(dealEtapa) === 'handover'
}

export function GestorEntradaDetailContent({ entrada, onUpdated }: Props) {
  const [savingStep, setSavingStep] = useState<ManualEntradaStepKey | null>(null)
  const [notes, setNotes] = useState<Record<ManualEntradaStepKey, string>>(() => {
    const initial = {} as Record<ManualEntradaStepKey, string>
    for (const key of MANUAL_ENTRADA_STEP_KEYS) {
      initial[key] = entrada.steps[key].note ?? ''
    }
    return initial
  })

  const sla = formatSla(entrada.slaHandoverDueAt)
  const showHandoverSla = sla && isDealAwaitingHandoverCompletion(entrada.deal.etapa)

  const toggleStep = async (stepKey: ManualEntradaStepKey, done: boolean) => {
    setSavingStep(stepKey)
    try {
      const updated = await gestoresService.updateEntradaStep(entrada.id, stepKey, {
        done,
        note: notes[stepKey] || null,
      })
      onUpdated(updated)
      toast.success(done ? 'Etapa concluída' : 'Etapa reaberta')
    } catch {
      toast.error('Não foi possível atualizar a etapa')
    } finally {
      setSavingStep(null)
    }
  }

  const saveNote = async (stepKey: ManualEntradaStepKey) => {
    setSavingStep(stepKey)
    try {
      const updated = await gestoresService.updateEntradaStep(entrada.id, stepKey, {
        done: entrada.steps[stepKey].done,
        note: notes[stepKey] || null,
      })
      onUpdated(updated)
      toast.success('Observação salva')
    } catch {
      toast.error('Não foi possível salvar a observação')
    } finally {
      setSavingStep(null)
    }
  }

  return (
    <div className='space-y-6'>
      <div className='glass-card rounded-xl border border-white/[0.06] px-5 py-5 sm:px-6'>
        <p className='text-xs font-semibold uppercase tracking-wider text-accent'>
          {GESTOR_SETOR_LABELS[entrada.setor] ?? entrada.setor}
        </p>
        <h2 className='mt-1 text-xl font-bold text-text-primary'>{entrada.empresa.nome}</h2>
        <p className='mt-1 text-sm text-text-muted'>
          {entrada.deal.nome} · Closer {entrada.deal.owner.name}
        </p>
        {entrada.servicosLabel && (
          <p className='mt-2 text-sm text-text-primary'>Serviços: {entrada.servicosLabel}</p>
        )}
        {showHandoverSla && (
          <p
            className={cn(
              'mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold',
              sla.overdue ? 'bg-danger/10 text-danger' : 'bg-amber-500/10 text-amber-700',
            )}
          >
            {sla.label}
          </p>
        )}
      </div>

      <GestorEntradaKanbanSync dealEtapa={entrada.deal.etapa} steps={entrada.steps} />

      <div>
        <h3 className='mb-3 text-sm font-semibold text-text-primary'>Suas etapas operacionais</h3>
        <ol className='space-y-3'>
          {MANUAL_ENTRADA_STEP_KEYS.map((stepKey, index) => {
            const step = entrada.steps[stepKey]
            const isSaving = savingStep === stepKey
            return (
              <li
                key={stepKey}
                className='glass-card rounded-xl border border-white/[0.06] p-4'
              >
                <div className='flex items-start gap-3'>
                  <div className='mt-0.5 shrink-0'>
                    {step.done ? (
                      <CheckCircle2 size={20} className='text-emerald-500' />
                    ) : (
                      <Circle size={20} className='text-text-muted' />
                    )}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='text-sm font-semibold text-text-primary'>
                      {index + 1}. {ENTRADA_STEP_LABELS[stepKey]}
                    </p>
                    <p className='mt-1 text-xs leading-relaxed text-text-muted'>
                      {ENTRADA_STEP_DESCRIPTIONS[stepKey]}
                    </p>
                    {step.doneAt && (
                      <p className='mt-2 text-[11px] text-text-muted'>
                        Concluído em {new Date(step.doneAt).toLocaleString('pt-BR')}
                      </p>
                    )}
                    <textarea
                      value={notes[stepKey]}
                      onChange={(e) =>
                        setNotes((prev) => ({ ...prev, [stepKey]: e.target.value }))
                      }
                      placeholder='Observações desta etapa…'
                      rows={2}
                      className='mt-3 w-full rounded-lg border border-white/[0.08] bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/40'
                    />
                    <div className='mt-3 flex flex-wrap gap-2'>
                      <Button
                        type='button'
                        size='sm'
                        variant={step.done ? 'secondary' : 'primary'}
                        disabled={isSaving}
                        onClick={() => void toggleStep(stepKey, !step.done)}
                      >
                        {isSaving ? (
                          <Loader2 size={14} className='animate-spin' />
                        ) : step.done ? (
                          'Reabrir etapa'
                        ) : (
                          'Marcar como concluída'
                        )}
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='secondary'
                        disabled={isSaving}
                        onClick={() => void saveNote(stepKey)}
                      >
                        Salvar observação
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}
